import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchAuditLogs } from '@/store/slices/adminSlice';
import { Search, Filter, ShieldCheck, ChevronLeft, ChevronRight, FileCode2, ExternalLink } from 'lucide-react';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import Table from '@/components/common/Table';
import FilterPanel from '@/components/common/FilterPanel';
import SelectDropdown from '@/components/common/SelectDropdown';
import Modal from '@/components/common/Modal';
import { toast } from 'react-toastify';

// Utility formatters map
const ENTITY_TYPES = ['Event', 'Route', 'User', 'Organizer', 'Sponsor', 'SponsorshipInterest'];

// Maps an audit-log entity type to the admin list page path
const ENTITY_ROUTE_MAP = {
  event: '/admin/events',
  route: '/admin/routes',
  user: '/admin/users',
  organizer: '/admin/organizers',
  sponsor: '/admin/sponsors',
  sponsorshipinterest: '/admin/events',  // No dedicated sponsorship-interest list; fall back to events
};

const AdminAuditDashboard = () => {
  const dispatch = useDispatch();
  const { auditLogs, auditLogsHasNext, auditLogsCursor, isLoadingAuditLogs } = useSelector(state => state.admin);

  const [filters, setFilters] = useState({
    limit: 20,
    action: '',
    entity_type: '',
  });

  const [actionFilter, setActionFilter] = useState('');
  const [selectedMetadata, setSelectedMetadata] = useState(null);

  useEffect(() => {
    dispatch(fetchAuditLogs({ ...filters, action: actionFilter }));
  }, [actionFilter, filters, dispatch]);

  useEffect(() => {
    dispatch(fetchAuditLogs(filters));
  }, [filters, dispatch]);

  const handleLoadMore = () => {
    if (auditLogsHasNext && auditLogsCursor) {
      dispatch(fetchAuditLogs({ ...filters, action: actionFilter, cursor: auditLogsCursor }));
    }
  };

  const clearFilters = () => {
    setFilters({ limit: 20, action: '', entity_type: '' });
    setActionFilter('');
  };

  const columns = [
    { key: "timestamp", title: "Timestamp" },
    { key: "actor", title: "Actor" },
    { key: "severity", title: "Severity" },
    { key: "action", title: "Action" },
    { key: "entity", title: "Entity Type" },
    { key: "details", title: "Details Summary" },
  ];


  const renderRow = (log) => (
    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
        {new Date(log.timestamp).toLocaleString('en-IN', {
          day: 'numeric', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        })}
      </td>
      <td className="px-4 py-4">
        <div className="font-medium text-gray-900 dark:text-white">
          {log.actor?.name || 'System'}
        </div>
        <div className="text-xs text-gray-500 capitalize">{log.actor?.role || 'System Action'}</div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap">
        <Badge tone={log.severity === 'error' ? 'danger' : log.severity === 'warning' ? 'warning' : 'info'} className="uppercase text-[10px] font-bold tracking-wider">
          {log.severity || 'info'}
        </Badge>
      </td>
      <td className="px-4 py-4 whitespace-nowrap">
        <span className="font-medium text-sm text-gray-800 dark:text-gray-200 capitalize">
          {log.action?.split(/[\._]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
        </span>
      </td>
      <td className="px-4 py-4 whitespace-nowrap">
        <span className="font-medium rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-1 text-xs text-gray-800 dark:text-gray-200 capitalize w-max block mb-1">
          {log.entity?.type}
        </span>
        {log.entity?.id ? (() => {
          const entityTypeKey = log.entity.type?.toLowerCase() || '';
          const listPath = ENTITY_ROUTE_MAP[entityTypeKey];
          const shortId = `${log.entity.id.substring(0, 8)}...`;
          return listPath ? (
            <Link
              to={`${listPath}?id=${log.entity.id}`}
              className="flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-mono mt-1 transition-colors group"
              title={`Open ${log.entity.type} ${log.entity.id}`}
            >
              <span className="truncate max-w-[80px]">{shortId}</span>
              <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity shrink-0" />
            </Link>
          ) : (
            <div className="text-[10px] text-gray-400 mt-1 font-mono truncate max-w-[100px]" title={log.entity?.id}>
              {shortId}
            </div>
          );
        })() : (
          <div className="text-[10px] text-gray-400 mt-1 italic">—</div>
        )}
      </td>
      <td className="px-4 py-4 min-w-[250px] max-w-sm">
        <div className="space-y-2">
          {/* Inline Metadata Formatting */}
          {log.metadata ? (
             <div className="flex flex-col gap-1.5">
               
               {/* 1. State Transitions */}
               {log.metadata.from && log.metadata.to && (
                 <div className="flex items-center gap-1.5 font-mono text-[11px]">
                   <span className="text-gray-500 font-sans text-xs">Transition:</span>
                   <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-gray-600 dark:text-gray-400 truncate max-w-[90px]">{log.metadata.from}</span>
                   <span className="text-gray-400 shrink-0">➔</span>
                   <span className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded truncate max-w-[90px]">{log.metadata.to}</span>
                 </div>
               )}

               {/* 2. Reason Blocks */}
               {log.metadata.reason && (
                 <div className="flex flex-col gap-0.5">
                   <span className="text-xs text-gray-500 font-medium">Reason:</span>
                   <div className="text-xs text-gray-600 dark:text-gray-300 italic bg-gray-50 dark:bg-gray-800/50 border-l-2 border-gray-300 dark:border-gray-600 pl-2 py-1 line-clamp-2" title={log.metadata.reason}>
                     "{log.metadata.reason}"
                   </div>
                 </div>
               )}

               {/* 3. Changed Fields / Diffs */}
               {log.metadata.metadata?.diffs ? (
                 <div className="flex flex-col gap-1 mt-1">
                   <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Changes</span>
                   <div className="flex flex-wrap gap-2">
                     {Object.entries(log.metadata.metadata.diffs).map(([field, {before, after}]) => (
                       <div key={field} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-[10px] flex flex-col min-w-[120px] max-w-[200px]">
                         <div className="bg-gray-50 dark:bg-gray-700/50 px-1.5 py-0.5 border-b border-gray-200 dark:border-gray-700 font-mono text-gray-600 dark:text-gray-300 font-semibold truncate">{field}</div>
                         <div className="px-1.5 py-1 flex flex-col gap-0.5">
                           <div className="text-red-600 dark:text-red-400 line-through truncate" title={JSON.stringify(before)}>{typeof before === 'object' ? JSON.stringify(before) : String(before)}</div>
                           <div className="text-green-600 dark:text-green-400 truncate" title={JSON.stringify(after)}>{typeof after === 'object' ? JSON.stringify(after) : String(after)}</div>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               ) : log.metadata.metadata?.changedFields && (
                 <div className="flex flex-wrap gap-1 mt-1 items-center">
                   <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mr-1">Updated</span>
                   {log.metadata.metadata.changedFields.map(f => (
                     <span key={f} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800/80 rounded text-gray-600 dark:text-gray-300 text-[10px] font-mono">{f}</span>
                   ))}
                 </div>
               )}

               {/* 4. View Full Details Button */}
               {Object.keys(log.metadata).length > 0 && (
                 <button 
                   onClick={() => setSelectedMetadata(log.metadata)}
                   className="text-[10px] w-max uppercase tracking-wider text-blue-600 dark:text-blue-400 hover:text-blue-800 font-bold mt-1"
                 >
                   View Full Payload
                 </button>
               )}
             </div>
          ) : (
             <span className="text-xs text-gray-400 italic">No additional details</span>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-blue-600" />
            Audit Logs Dashboard
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Track and monitor administrative actions across the platform.
          </p>
        </div>
        <div className="flex items-center gap-2">
        </div>
      </div>

      <FilterPanel
        title="Audit Logs Filters"
        defaultOpen={true}
        hasActiveFilters={!!actionFilter || !!filters.entity_type}
        onClearFilters={clearFilters}
      >
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1 w-full md:w-64 max-w-xs">
            <SelectDropdown
              options={[
                { value: '', label: 'All Actions' },
                { value: 'create', label: 'Create' },
                { value: 'update', label: 'Update' },
                { value: 'delete', label: 'Delete' },
                { value: 'approve', label: 'Approve' },
                { value: 'reject', label: 'Reject' },
                { value: 'publish', label: 'Publish' },
                { value: 'archive', label: 'Archive' },
              ]}
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="All Actions"
            />
          </div>
          <div className="w-full md:w-64">
            <SelectDropdown
              options={[
                { value: '', label: 'All Entity Types' },
                ...ENTITY_TYPES.map(t => ({ value: t, label: t }))
              ]}
              value={filters.entity_type}
              onChange={(e) => setFilters(p => ({ ...p, entity_type: e.target.value }))}
              placeholder="All Entity Types"
            />
          </div>
        </div>
      </FilterPanel>

        {isLoadingAuditLogs && auditLogs.length === 0 ? (
          <div className="py-12 flex flex-col items-center justify-center text-gray-500 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-4"></div>
            Loading audit logs...
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="py-16 flex flex-col items-center justify-center text-gray-500 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <FileCode2 className="w-12 h-12 mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No Logs Found</h3>
            <p className="text-sm">Try adjusting your filters or search terms.</p>
            <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4">
               Clear Filters
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
             <Table columns={columns} data={auditLogs} renderRow={renderRow} />
              {auditLogsHasNext && (
               <div className="flex justify-center p-4">
                 <Button 
                   onClick={handleLoadMore} 
                   variant="outline" 
                   size="sm"
                   disabled={isLoadingAuditLogs}
                 >
                   {isLoadingAuditLogs ? 'Loading...' : 'Load More Context'}
                 </Button>
               </div>
             )}
          </div>
        )}

      <Modal 
        open={!!selectedMetadata} 
        onClose={() => setSelectedMetadata(null)}
        title="Details"
      >
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md border border-gray-200 dark:border-gray-700 overflow-x-auto divide-y divide-gray-200 dark:divide-gray-700">
           <div className="space-y-3">
             {selectedMetadata && Object.entries(selectedMetadata).map(([key, value]) => (
                <div key={key} className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 last:border-0 border-b border-gray-200 dark:border-gray-700">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 capitalize">
                    {key.replace(/_/g, ' ')}
                  </div>
                  <div className="md:col-span-2 text-sm text-gray-900 dark:text-gray-100 font-mono break-all">
                    {key === 'metadata' && value?.diffs ? (
                      <div className="space-y-4">
                        {value.changedFields && (
                          <div className="text-xs text-gray-500">
                            <span className="font-semibold text-gray-700 dark:text-gray-300 mr-2">Updated fields:</span>
                            {value.changedFields.join(', ')}
                          </div>
                        )}
                        <div className="space-y-2 mt-2">
                          {Object.entries(value.diffs).map(([f, diff]) => (
                            <div key={f} className="text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-900/50 p-2 flex flex-col gap-1 shadow-sm">
                              <span className="font-bold text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700 pb-1 mb-1">{f}</span>
                              <div className="text-red-600 bg-red-50 dark:bg-red-900/10 px-1.5 py-0.5 rounded break-all">- {typeof diff.before === 'object' ? JSON.stringify(diff.before) : String(diff.before)}</div>
                              <div className="text-green-600 bg-green-50 dark:bg-green-900/10 px-1.5 py-0.5 rounded break-all">+ {typeof diff.after === 'object' ? JSON.stringify(diff.after) : String(diff.after)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (value === null || value === undefined || value === '') 
                      ? <span className="text-gray-400 italic">Not provided</span> 
                      : typeof value === 'object'
                        ? JSON.stringify(value, null, 2)
                        : String(value)}
                  </div>
                </div>
             ))}
           </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminAuditDashboard;
